import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { FilterBar } from '../components/hub/FilterBar';
import { DirectoryColumns } from '../components/hub/DirectoryColumns';
import { HubFaqs } from '../components/hub/HubFaqs';
import { SiblingHubs } from '../components/hub/SiblingHubs';
import { DesignNote } from '../components/DesignNote';
import { birthdaysIntro, partiesFaqs, partiesTier3, partyFilters, routes, shortAnswer, timeline, venues } from '../data/birthdays';
export function Birthdays() {
  const [active, setActive] = useState<string[]>([]);
  function toggle(option: string) {
    setActive(prev => prev.includes(option) ? prev.filter(x => x !== option) : [...prev, option]);
  }
  const filtered = useMemo(() => {
    if (active.length === 0) return venues;
    return venues.filter(v => active.every(f => v.tags.includes(f)));
  }, [active]);
  return <main id="main" className="bg-paper" data-id="element-518" data-mp="11uj24pt1rp5s">
      <div className="mx-auto max-w-shell px-6 pb-12 pt-8 lg:px-10" data-id="element-519" data-mp="3lmv4eebdrmof">
        <nav aria-label="Breadcrumb" data-id="element-520" data-mp="3qmpx8nepumc4">
          <ol className="flex flex-wrap items-center gap-2 text-[12.5px] text-muted" data-id="element-521" data-mp="3qnr5yl89ewqo">
            <li data-id="element-522" data-mp="3bqs4zg7wy256">
              <Link to="/" className="hover:text-ink hover:underline" data-id="element-523" data-mp="3br9xc1rgfw7t">
                Home
              </Link>
            </li>
            <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-524" data-mp="3gqmvhins0ppp" />
            <li data-id="element-525" data-mp="89iaf1s6e2gh">
              <Link to="/things-to-do" className="hover:text-ink hover:underline" data-id="element-526" data-mp="8b33emyuddc8">
                Things to do
              </Link>
            </li>
            <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-527" data-mp="3upcreuyv0jch" />
            <li aria-current="page" className="text-ink" data-id="element-528" data-mp="3i08obibvxug">
              Birthday parties
            </li>
          </ol>
        </nav>

        <DesignNote label="A cross-cut guide — no venue lives here" note="Every venue below already exists on Parks, Attractions or Classes. This page gathers them under the search parents actually make and links back to each one. “Birthday parties” is a tag, so this list updates itself." data-id="element-529" data-mp="r6t7cfqc4jsr" />

        <div className="max-w-3xl" data-id="element-530" data-mp="10vzyotee8ls7">
          <p className="label text-olive" data-id="element-531" data-mp="wtv2po0h3d2e">{birthdaysIntro.meta}</p>
          <h1 className="mt-6 font-display text-[48px] font-300 leading-[0.98] text-ink sm:text-[66px]" data-id="element-532" data-mp="ws1x8c1liyvz">
            Kids birthday party places in{' '}
            <span className="italic" data-id="element-533" data-mp="mw8iqn4bgvbk">Santa Barbara</span>
          </h1>
          <p className="mt-7 max-w-prose text-[18px] font-300 leading-[1.7] text-ink/75" data-id="element-534" data-mp="i4yyy35g8m7l">
            {birthdaysIntro.deck}
          </p>
          <p className="mt-7 text-[13px] text-muted" data-id="element-535" data-mp="d6xg9jbxqown">
            By {birthdaysIntro.author} · {birthdaysIntro.published}
          </p>
        </div>

        <img src={birthdaysIntro.hero} alt={birthdaysIntro.heroAlt} className="photo-soft mt-14 aspect-[21/9] w-full object-cover" data-id="element-536" data-mp="92harj68hjhy" />
      </div>

      <ShortAnswer data-id="element-537" data-mp="3vcaev1m5nq7i" />
      <Routes data-id="element-538" data-mp="3vdv183ybg2ua" />

      <FilterBar filters={partyFilters} active={active} onToggle={toggle} data-id="element-539" data-mp="2atq4xj7sjoz2" />

      <section aria-labelledby="venues-h" className="bg-paper" data-id="element-540" data-mp="2avu79wq1kfx8">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-541" data-mp="2fr9ngvbiunwt">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-542" data-mp="2kre36j4lvelz">
            <h2 id="venues-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-543" data-mp="2oszbrsa3x6qd">
              Every venue, compared
            </h2>
            <p className="label text-muted" role="status" data-id="element-544" data-mp="1x1a0qf35lkwk">
              {filtered.length} {filtered.length === 1 ? 'venue' : 'venues'}
              {active.length > 0 && ` · ${active.length} filters`}
            </p>
          </div>

          {filtered.length === 0 ? <div className="mt-14 max-w-lg" data-id="element-545" data-mp="19ch2ud2z12zb">
              <h3 className="font-display text-[30px] font-300 text-ink" data-id="element-546" data-mp="19bpftqnfwdkn">
                Nothing matches all of that
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-547" data-mp="1dbptva6mmmr6">
                Try dropping the budget filter — the hosted venues all sit in a
                similar range.
              </p>
              <button type="button" onClick={() => setActive([])} className="label mt-8 bg-ink px-7 py-3.5 text-paper transition-colors duration-150 ease-soft hover:bg-olive" data-id="element-548" data-mp="p8jjn8xppk2p">
                Clear filters
              </button>
            </div> : <ul className="mt-12 border-t border-line" data-id="element-549" data-mp="181dnb6pvxprm">
              {filtered.map(venue => <li key={venue.id} data-id="element-550" data-mp="1myo50xt4frvs">
                  <article className="grid gap-y-6 border-b border-line py-9 lg:grid-cols-[1.5fr_1fr] lg:gap-x-16" data-id="element-551" data-mp="1mxmj1yakhkza">
                    <div data-id="element-552" data-mp="1hwytzi5wfe7p">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-553" data-mp="3ophnptyeop80">
                        <h3 className="font-display text-[29px] font-300 leading-tight text-ink" data-id="element-554" data-mp="3orcbrh4z4xgb">
                          {venue.name}
                        </h3>
                        <span className="label text-olive" data-id="element-555" data-mp="1z8vxfye032rs">{venue.type}</span>
                      </div>
                      <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-556" data-mp="246npqtik4qwi">
                        {venue.blurb}
                      </p>
                      <p className="mt-5 text-[13px] text-muted" data-id="element-557" data-mp="1uh48tteen74a">
                        Full listing on{' '}
                        <Link to={venue.livesOnHref} className="text-ink underline decoration-olive underline-offset-4 hover:text-olive" data-id="element-558" data-mp="1ggtdad25h4cs">
                          {venue.livesOn}
                        </Link>
                      </p>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-8 gap-y-4 self-start" data-id="element-559" data-mp="1bkvwy6qvzy00">
                      {[['Cost', venue.cost], ['Fits', venue.capacity], ['Length', venue.duration], ['Ages', venue.ages], ['Who cleans up', venue.cleanup]].map(([term, value]) => <div key={term} data-id="element-560" data-mp="2zix1ulru5k2l">
                          <dt className="label text-muted/70" data-id="element-561" data-mp="2ugo6jirhq5xb">{term}</dt>
                          <dd className="mt-1.5 text-[14px] leading-snug text-ink" data-id="element-562" data-mp="2piec5adlb9cr">
                            {value}
                          </dd>
                        </div>)}
                    </dl>
                  </article>
                </li>)}
            </ul>}
        </div>
      </section>

      <Timeline data-id="element-563" data-mp="3df0pfw87eu63" />
      <SponsorSlot data-id="element-564" data-mp="3deioimaljrru" />

      <DirectoryColumns title="Entertainers, cakes and everything else" groups={partiesTier3} data-id="element-565" data-mp="3ddz6l5ky53dt" />
      <HubFaqs faqs={partiesFaqs} data-id="element-566" data-mp="9qyu1ce9ew7s" />
      <SiblingHubs exclude={['Things to do']} data-id="element-567" data-mp="9s0e26d96t1n" />
    </main>;
}
function ShortAnswer() {
  return <section aria-labelledby="short-h" className="bg-foam/40" data-id="element-568" data-mp="4zqj2bxa72si">
      <div className="mx-auto grid max-w-shell gap-x-20 gap-y-12 px-6 py-16 lg:grid-cols-[1.3fr_1fr] lg:px-10 lg:py-20" data-id="element-569" data-mp="d9o3juat5d70">
        <div data-id="element-570" data-mp="hbstoj21rrow">
          <h2 id="short-h" className="font-display text-[34px] font-300 leading-tight text-ink" data-id="element-571" data-mp="hb9om2xhwxsa">
            {shortAnswer.title}
          </h2>
          <p className="mt-6 max-w-prose text-[17px] leading-[1.9] text-ink/85" data-id="element-572" data-mp="r9yvz1rrmzua">
            {shortAnswer.body}
          </p>
        </div>

        <dl className="self-start border-t border-ink/20" data-id="element-573" data-mp="w5xrraffbk4q">
          {shortAnswer.picks.map(pick => <div key={pick.label} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-ink/15 py-5" data-id="element-574" data-mp="10797mo8hwqzq">
              <dt className="label text-ink/60" data-id="element-575" data-mp="19yndreuycp1u">{pick.label}</dt>
              <dd className="font-display text-[20px] font-400 leading-snug text-ink" data-id="element-576" data-mp="1ezb561n3f7wl">
                {pick.value}
              </dd>
            </div>)}
        </dl>
      </div>
    </section>;
}
function Routes() {
  return <section aria-labelledby="routes-h" className="border-t border-line bg-white" data-id="element-577" data-mp="256qbqqwykaov">
      <div className="mx-auto max-w-shell px-6 py-16 lg:px-10 lg:py-20" data-id="element-578" data-mp="208yj1uohokjm">
        <DesignNote label="Sorted by the problem, not the venue type" note="Budget and how much work it makes for you are the two axes parents actually decide on. Lead with those." data-id="element-579" data-mp="2e9hqp0p1lol9" />
        <h2 id="routes-h" className="max-w-2xl font-display text-[36px] font-300 leading-[1.06] text-ink sm:text-[44px]" data-id="element-580" data-mp="1llltluvk9uny">
          Four ways to do this
        </h2>

        <ul className="mt-14 grid gap-x-14 gap-y-12 border-t border-line pt-12 sm:grid-cols-2 lg:grid-cols-4" data-id="element-581" data-mp="1vbf4etnvq5r5">
          {routes.map(route => <li key={route.slug} className="group relative" data-id="element-582" data-mp="1qhu2wrn2utn7">
              <span className={`block h-2 w-14 ${route.swatch}`} aria-hidden="true" data-id="element-583" data-mp="12srnlqfx1cvv" />
              <h3 className="mt-6 font-display text-[26px] font-300 leading-tight text-ink" data-id="element-584" data-mp="xtyc23k6vvyc">
                <a href="#venues-h" className="decoration-olive decoration-1 underline-offset-[7px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-585" data-mp="39i37ppd89nai">
                  {route.name}
                </a>
              </h3>
              <p className="mt-4 text-[14.5px] leading-[1.8] text-muted" data-id="element-586" data-mp="35fyf8tyimwpg">
                {route.blurb}
              </p>
              <dl className="mt-6 flex gap-x-8 border-t border-line pt-4" data-id="element-587" data-mp="3t7naoj6mw1am">
                <div data-id="element-588" data-mp="12t8qq4mwy6y">
                  <dt className="label text-muted/70" data-id="element-589" data-mp="11894m3gqp28">Budget</dt>
                  <dd className="mt-1.5 text-[13.5px] text-ink" data-id="element-590" data-mp="3j8o210lvc5nm">{route.budget}</dd>
                </div>
                <div data-id="element-591" data-mp="3o6prq4dsv382">
                  <dt className="label text-muted/70" data-id="element-592" data-mp="3o5xy2z3rjfkf">Effort</dt>
                  <dd className="mt-1.5 text-[13.5px] text-ink" data-id="element-593" data-mp="27t97e3h4tpvo">{route.effort}</dd>
                </div>
              </dl>
            </li>)}
        </ul>
      </div>
    </section>;
}
function Timeline() {
  return <section aria-labelledby="timeline-h" className="border-t border-line bg-white" data-id="element-594" data-mp="hggxjsgr42y4">
      <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-595" data-mp="mes67zvb87er">
        <h2 id="timeline-h" className="max-w-2xl font-display text-[36px] font-300 leading-[1.06] text-ink sm:text-[44px]" data-id="element-596" data-mp="7pmcusj5lbes">
          When to book what
        </h2>
        <p className="mt-5 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-597" data-mp="3uubvknsv15oj">
          The single most common mistake is starting six weeks out. By then the
          shaded park areas and the Saturday gym slots are gone.
        </p>

        <ol className="mt-14 border-t border-line" data-id="element-598" data-mp="3kx66fl3t3oqy">
          {timeline.map(step => <li key={step.when} className="grid gap-y-3 border-b border-line py-8 lg:grid-cols-[200px_260px_1fr] lg:gap-x-14" data-id="element-599" data-mp="3pufsqrvwfkak">
              <p className="label pt-1 text-olive" data-id="element-600" data-mp="1o3uj1cjm9849">{step.when}</p>
              <h3 className="font-display text-[24px] font-300 leading-snug text-ink" data-id="element-601" data-mp="1o330p3f156vx">
                {step.what}
              </h3>
              <p className="max-w-prose text-[15px] leading-[1.8] text-muted" data-id="element-602" data-mp="33mtxiui0rj4q">
                {step.detail}
              </p>
            </li>)}
        </ol>
      </div>
    </section>;
}
function SponsorSlot() {
  return <section aria-label="Sponsored message" className="bg-creme/60" data-id="element-603" data-mp="3djykgwjoviml">
      <div className="mx-auto flex max-w-shell flex-wrap items-end justify-between gap-8 px-6 py-14 lg:px-10" data-id="element-604" data-mp="3rc4mzftx5r3k">
        <div className="max-w-xl" data-id="element-605" data-mp="3mdtfvcv0dup1">
          <p className="label text-ink/55" data-id="element-606" data-mp="3mcs1ayesaq2k">Sponsored by a local business</p>
          <h2 className="mt-4 font-display text-[30px] font-300 leading-tight text-ink" data-id="element-607" data-mp="55g3u6fomurd">
            Parties at SB Gymnastics, two coaches included
          </h2>
          <p className="mt-3 text-[15px] leading-[1.8] text-ink/70" data-id="element-608" data-mp="26wjir7wluvhq">
            Ninety minutes in the gym, thirty in the party room, and you carry
            nothing in or out. Saturday slots book eight weeks ahead.
          </p>
        </div>
        <a href="#faq" className="label shrink-0 border border-ink/30 px-7 py-3.5 text-ink transition-colors duration-150 ease-soft hover:bg-ink hover:text-paper" data-id="element-609" data-mp="2rmncvdtui0gv">
          See party packages
        </a>
      </div>
    </section>;
}